import { isSuccess } from '@devexperts/remote-data-ts'
import chroma from 'chroma-js'
import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@/hooks/store.ts'
import { selectIsWhiteLabelActive, widgetSlice } from '@/store/widgetSlice.ts'
import { getColorShades } from '@/utils/getColorShades'
import { setDocumentVariables } from '@/utils/setDocumentVariables'

/**
 * This hook is responsible for fetching the whiteLabel data when the mode set is “widget” and setting
 * the body class and the global css variables from the data returned in response
 */
export const useSetupWhiteLabel = () => {
  const dispatch = useAppDispatch()
  const whiteLabel = useAppSelector(widgetSlice.selectors.whiteLabel)
  const isActive = useAppSelector(selectIsWhiteLabelActive)
  const [searchParams] = useSearchParams()

  useEffect(() => {
    if (!isActive) return

    dispatch(
      widgetSlice.actions.fetchWidgetWhiteLabel({
        previewCode: searchParams.get('previewCode'),
      })
    )
  }, [isActive])

  useEffect(() => {
    if (!isSuccess(whiteLabel) || !whiteLabel.value) return

    const {
      headerTextColor,
      headerBackgroundColor,
      textColorBackground,
      primaryColor,
      pageBackgroundColor,
      dangerButtonBackgroundColor,
      dangerButtonTextColor,
      borderRadius,
      textFont,
    } = whiteLabel.value

    document.body.classList.add('whitelabel')

    if (!borderRadius) {
      document.body.classList.add('whitelabel--no-border-radius')
    }

    let variables: [string, string | number][] = [
      ['--white-label-text-font', textFont || ''],
      ['--white-label-header-background-color', headerBackgroundColor || ''],
      ['--white-label-header-text-color', headerTextColor || ''],
      ['--white-label-text-color-bg-image', textColorBackground || ''],
      ['--white-label-primary-color', primaryColor || ''],
      [
        '--white-label-danger-button-bg-color',
        dangerButtonBackgroundColor || '',
      ],
      ['--white-label-danger-button-text-color', dangerButtonTextColor || ''],
      ['--white-label-page-background-color', pageBackgroundColor || ''],

      /* Spit ui & Datepicker */
      ['--wl-font-family', textFont || ''],
    ]

    if (!borderRadius) {
      variables = [...variables, ['--wl-border-radius', 0]]
    }

    if (primaryColor) {
      variables = [...variables, ...getColorShades(primaryColor)]
    }

    if (dangerButtonBackgroundColor) {
      const dangerShades = chroma
        .scale(['#fff', dangerButtonBackgroundColor, '#000'])
        .mode('lab')
        .colors(10)

      const [
        [
          dangerBackgroundColorRgb,
          dangerBackgroundColorHoverRgb,
          dangerTextColorRgb,
        ],
      ] = [
        [
          chroma(dangerButtonBackgroundColor).rgb(),
          chroma(dangerShades[7]).rgb(),
          chroma(dangerButtonTextColor).rgb(),
        ],
      ]

      variables = [
        ...variables,
        ['--wl-danger-btn-bg-color-rgb', dangerBackgroundColorRgb.join(' ')],
        [
          '--wl-danger-btn-bg-color-hover-rgb',
          dangerBackgroundColorHoverRgb.join(' '),
        ],
        ['--wl-danger-btn-text-color-rgb', dangerTextColorRgb.join(' ')],
      ]
    }

    return setDocumentVariables({
      callback: () => {
        document.body.classList.remove('whitelabel')

        if (!borderRadius) {
          document.body.classList.remove('whitelabel--no-border-radius')
        }
      },
      variables,
    })
  }, [whiteLabel])
}
